/**
 * As rotas que o roteador registra.
 *
 * <p>Existe como constante para que um teste possa confrontá-la com o que a
 * navegação oferece. Sem isso, marcar um item como disponível apontando para
 * uma rota que ninguém criou é um erro que só aparece quando alguém clica e
 * cai numa tela em branco — e ninguém clica em tudo antes de publicar.
 */
export const ROTAS = {
  painel: "/",
  entrar: "/entrar",
  segundoFator: "/entrar/codigo",
  fornecedores: "/regulatorio/fornecedores",
  lotes: "/producao/materias-primas",
  /**
   * A ficha de UM lote — o destino do QR da etiqueta.
   *
   * <p>Por **código**, e não por identificador: o código é o que está impresso
   * no tambor, e é o token do resolvedor `/qr/<token>`
   * ([ADR-0006](../../../../../docs/decisoes/0006-urls-publicas-e-qr-code.md)).
   *
   * <p>⚠️ Dizia `/r/<token>` até 2026-08-14. O caminho vivo é `/qr/`; `/r/`
   * responde **301**, porque adesivo colado em tambor não se reimprime.
   *
   * <p>⚠️ Esta rota é o alvo de um endereço que já saiu impresso. Ela **pode**
   * mudar — é justamente isso que o resolvedor compra —, mas quem a mudar tem de
   * mudar junto o `CAMINHO_DA_TELA` de `LoteComoAlvoPublico`, no backend. Os
   * dois são as duas pontas do mesmo redirecionamento.
   */
  lote: "/producao/materias-primas/lote/:codigo",
  /**
   * A ficha de UM VOLUME — **o destino do QR impresso na etiqueta**.
   *
   * <p>Desde que o token passou a ser por volume (ADR-0006), é esta a rota que
   * o adesivo abre: ela sabe qual dos vinte tambores está na mão. A de `lote`
   * continua sendo o caminho manual, pelo código impresso em letra grande.
   *
   * <p>⚠️ Esta rota é o alvo de um endereço que já saiu impresso. Ela **pode**
   * mudar — é justamente isso que o resolvedor compra —, mas quem a mudar tem
   * de mudar junto o `CAMINHO_DA_TELA` de `VolumeComoAlvoPublico`, no backend.
   * Os dois são as duas pontas do mesmo redirecionamento.
   */
  volume: "/producao/materias-primas/volume/:token",
  /**
   * O lote aberto **pela lista**, em abas — uma seção por endereço (#36).
   *
   * <p>`resumo` · `volumes` · `etiqueta`. Cada uma tem endereço próprio, e é o
   * que faz recarregar não perder a aba e o link de uma aba se mandar.
   *
   * <p>⚠️ **Não confundir com `lote` e `volume` acima.** Aquelas são as telas
   * que o QR abre, são **de consulta e só** (feature 1.2.3, R1 e R10) e são
   * endereços **impressos**. Esta é a tela de quem veio da lista com o CNPJ
   * escolhido — e é ela que dá acesso à emissão.
   */
  lotePorId: "/producao/materias-primas/:id/:aba",
  /**
   * A aba de etiquetas — **e o endereço que já existia**.
   *
   * <p>`/producao/materias-primas/&lt;id&gt;/etiqueta?empresa=&lt;id&gt;` é o
   * endereço que o PO usa e guardou. Ele **não** virou redirecionamento: o
   * segmento `etiqueta`, no singular, foi mantido como o `:aba` da tela nova,
   * então a URL antiga **é** o endereço da aba. Um redirecionamento é uma linha
   * que alguém apaga daqui a seis meses sem saber o que ela segurava; um
   * endereço que não mudou não tem o que apagar.
   *
   * <p>Continua listada à parte porque é ela que a lista de lotes e o
   * recebimento navegam — e porque um teste a confronta com o que o roteador
   * registra.
   */
  etiqueta: "/producao/materias-primas/:id/etiqueta",
  /**
   * O parque de recipientes — o ativo físico, não o conteúdo.
   *
   * ⚠️ Esta rota **não** é alvo de endereço impresso. A plaqueta do tambor leva
   * o código em TEXTO e não leva QR, de propósito (ADR-0012 item 1): um segundo
   * símbolo no mesmo tambor é um símbolo que alguém escaneia por engano, e a
   * resposta errada chega parecendo certa. Ela pode mudar sem contrapartida no
   * backend — ao contrário de `lote` e `volume`.
   */
  recipientes: "/producao/recipientes",
} as const;

export const ROTAS_REGISTRADAS: readonly string[] = Object.values(ROTAS);
